// Card, SectionHeading and the zone grid (drawing v2.6, `.card`, `.sec-h`,
// `.zones` / `.zcol` on screen 7). A card is the white surface with radius 14
// and sh1; a section heading is 12px Gotham caps with an optional count and
// one link on the right. The zones of a record page sit in two independent
// columns (the contact page's column-major split) so a short card never
// leaves a hole beside a tall one; a wide zone spans both.

import type { ReactNode } from "react";
import { cx } from "./cx";

export function Card({ children, className, padded = false, id, as: Tag = "section", ...rest }: { children: ReactNode; className?: string | undefined; /** 20 by 24 record-card padding (phone 16). */ padded?: boolean | undefined; id?: string | undefined; as?: "section" | "div" | "article" | undefined; "aria-label"?: string | undefined; role?: string | undefined }) {
  return (
    <Tag className={cx("pg-card", padded && "pg-card--pad", className)} id={id} {...rest}>
      {children}
    </Tag>
  );
}

export function SectionHeading({ children, count, action, hint, level = 2, className }: { children: ReactNode; count?: ReactNode; action?: ReactNode; hint?: ReactNode; level?: 2 | 3; className?: string }) {
  const H = level === 3 ? "h3" : "h2";
  return (
    <div className={cx("pg-sec-h", className)}>
      <H className="pg-sec-h__title">
        {children}
        {count !== undefined && <span className="pg-count">{count}</span>}
      </H>
      {hint && <span className="pg-sec-h__hint">{hint}</span>}
      {action}
    </div>
  );
}

/** One customizable zone: a padded card with a section heading. */
export function ZoneCard({ title, count, action, children, className, id, wide }: { title?: ReactNode; count?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; id?: string; wide?: boolean }) {
  return (
    <Card className={cx("pg-zone", wide && "pg-zones__wide", className)} id={id}>
      {title && (
        <SectionHeading count={count} action={action}>
          {title}
        </SectionHeading>
      )}
      {children}
    </Card>
  );
}

/** The two-column zone grid; `wide` children span both columns. */
export function Zones({ children, className, label }: { children: ReactNode; className?: string; label?: string }) {
  return (
    <div className={cx("pg-zones", className)} aria-label={label}>
      {children}
    </div>
  );
}

export function ZoneColumn({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("pg-zcol", className)}>{children}</div>;
}

/** "Your layout of this page" and the Customize button above the zones. */
export function ZoneBar({ children, action }: { children: ReactNode; action: ReactNode }) {
  return (
    <div className="pg-zone-bar">
      <span>{children}</span>
      {action}
    </div>
  );
}

/** Key / value pairs inside a zone ("David Chen · Primary · 44"). */
export function KeyValues({ items, className }: { items: Array<{ key: ReactNode; value: ReactNode }>; className?: string }) {
  return (
    <dl className={cx("pg-kv", className)}>
      {items.map((it, i) => (
        <span key={i} style={{ display: "contents" }}>
          <dt>{it.key}</dt>
          <dd>{it.value}</dd>
        </span>
      ))}
    </dl>
  );
}
